import {
  TAssert,
  Severity,
  TLoggers,
  TLog,
  TGetLoggers,
  TAttachLoggers,
} from './types';

export function isNumber(value: any): value is number {
  return typeof value === 'number';
}

/**
 * Determines if a reference is a `String`.
 *
 * @param value Reference to check.
 */
export function isString(value: any): value is string {
  return typeof value === 'string';
}

/**
 * Determines if a reference is an Array.
 *
 * @param value Reference to check.
 */
export function isArray<T>(value: any): value is Array<T> {
  return Array.isArray(value);
}

let loggers: TLoggers = {};

export const attachLoggers: TAttachLoggers = (l) => {
  loggers = l;

  // Do not allow modifications to the object anymore.
  Object.freeze(l);
};

/**
 * Retreive a copy of currently attached loggers
 * This is mainly for testing purposes but maybe also useful
 * In case we need some manual logging.
 */
export const getLoggers: TGetLoggers = () => {
  return {
    ...loggers,
  };
};

if (process.env.NODE_ENV !== 'production') {
  // TODO: use a logging library
  attachLoggers({
    // eslint-disable-next-line no-console
    error: console.error,
    // eslint-disable-next-line no-console
    warn: console.warn,
    // eslint-disable-next-line no-console
    info: console.info,
  });
}

export const log: TLog = function (message, severity = 'ERROR') {
  const sev = Severity[severity];
  switch (sev) {
    case Severity.ERROR:
      loggers.error?.(message);
      break;
    case Severity.WARNING:
      loggers.warn?.(message);
      break;
    case Severity.INFO:
      loggers.info?.(message);
      break;
  }
};

export function getEnumKeysAsString<T extends Record<string, any>>(
  obj: T
): TStringKeyOf<T>[] {
  return Object.getOwnPropertyNames(obj).filter((key) => {
    return (
      Object.propertyIsEnumerable.call(obj, key) &&
      key !== String(parseFloat(key))
    );
  }) as TStringKeyOf<T>[];
}

export const assert: TAssert = function (
  condition: boolean,
  err: Error,
  severity = 'ERROR'
) {
  if (!condition) {
    if (process.env.NODE_ENV !== 'production') {
      const sev = Severity[severity];
      log('Assertion Error: ' + err.message, severity);
      if (sev === Severity.ERROR) {
        throw err;
      }
    } else {
      try {
        (window as any).newrelic.noticeError(err, {
          buildVersion: (window as any).BUILD_VERSION,
          errorHandler: 'assert',
          severity: Severity[severity],
        });
      } catch (e) {
        // Last resort - CS will possibly send a screenshot
        // eslint-disable-next-line no-console
        console.error('NR failure', e.message, err);
      }
    }
  }
};

export const isHex = (color: string): boolean =>
  /^#?([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/i.test(color);

export async function asyncForEach<T>(
  array: T[],
  callback: (item: T, index: number, array: T[]) => Promise<void>
): Promise<void> {
  for (let index = 0; index < array.length; index++) {
    await callback(array[index], index, array);
  }
}

export const validateRequired = (value: string): string => {
  let error = '';
  if (!value) {
    error = 'Required';
  }
  return error;
};

export const validateEmail = (value: string): string => {
  let error = '';
  if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(value)) {
    error = 'Invalid email address';
  }
  return error;
};

export const validateAlphaDash = (
  value: string,
  errorMessage?: string
): string => {
  let error = '';
  if (!/^[A-Z0-9_-]*$/i.test(value)) {
    error = errorMessage ?? 'Invalid value';
  }
  return error;
};

export const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const decimal = 1;
  const sizes = ['Bytes', 'kb', 'mb', 'gb', 'tb', 'pb', 'eb', 'zb', 'yb'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return (
    parseFloat((bytes / Math.pow(k, i)).toFixed(decimal)) + ' ' + sizes[i]
  );
};
